import React, { useCallback, useMemo } from 'react';

import { Box } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import Filters from 'components/Filters';
import InputWithDebouncedAction from 'components/InputWithDebouncedAction/InputWithDebouncedAction';
import processing_activities_messages from 'messages/processing_activities_messages';

import type { AssetRopaReviewFilterOptions, AssetRopaReviewFilters } from '../assetRopaReview.types';
import {
  assetRopaReviewFilterValuesToTableFilters,
  buildAssetRopaReviewAvailableFilters,
  mergeTableFiltersIntoAssetRopaReviewFilters,
  type AssetRopaReviewTableFilterState,
} from '../assetRopaReviewDrawerFilters';
import useStyles from '../ProcessingActivitiesAssetReviewsPage.styles';

type Props = {
  filterOptions: AssetRopaReviewFilterOptions;
  filters: AssetRopaReviewFilters;
  onFiltersChange: (filters: AssetRopaReviewFilters) => void;
  onSearch: (value: string) => void;
  searchValue: string;
};

const AssetRopaFiltersToolbar: React.FC<Props> = ({ filterOptions, filters, onFiltersChange, onSearch, searchValue }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const filterMessages = processing_activities_messages.asset_ropa.filters;

  const availableTableFilters = useMemo(() => buildAssetRopaReviewAvailableFilters(filterOptions), [filterOptions]);

  const tableFilters = useMemo(() => assetRopaReviewFilterValuesToTableFilters(filters, filterOptions), [filterOptions, filters]);

  const setTableFilters = useCallback(
    (updater: React.SetStateAction<AssetRopaReviewTableFilterState>) => {
      const currentTableFilters = assetRopaReviewFilterValuesToTableFilters(filters, filterOptions);
      const nextTableFilters = typeof updater === 'function' ? updater(currentTableFilters) : updater;
      onFiltersChange(mergeTableFiltersIntoAssetRopaReviewFilters(filters, nextTableFilters));
    },
    [filterOptions, filters, onFiltersChange],
  );

  return (
    <Box className={styles.toolbarSection}>
      <Box className={styles.ownerWorkspaceSearchRow}>
        <div className={styles.ownerWorkspaceSearchInputCell}>
          <InputWithDebouncedAction
            action={onSearch}
            className={styles.searchInputLabelEllipsis}
            formControl={{ size: 'small' }}
            label={t(filterMessages.search_placeholder)}
            value={searchValue}
          />
        </div>
        <Filters availableFilters={availableTableFilters} filters={tableFilters} setFilters={setTableFilters} />
      </Box>
    </Box>
  );
};

export default AssetRopaFiltersToolbar;
